// client/src/components/leagues/ScorePicker.js
import React from 'react'
import PropTypes from 'prop-types'
import { isValidSetScore } from '../../utils/tennisScoreValidation'
import { MATCH_FORMAT } from '../../constants/matchFormats'
import { getMatchFormatRules } from '../../utils/matchFormatRules'

/**
 * ScorePicker - Bottom sheet picker for selecting set scores
 * Provides dropdowns for both players' scores with validation
 */
export default function ScorePicker({
  visible,
  setIndex,
  sets,
  playerNames,
  onClose,
  onScoreChange,
  onClear,
  matchFormat,
}) {
  if (!visible || setIndex === null) {
    return null
  }

  const currentSet = sets[setIndex]
  const { maxGamesPerSet } = getMatchFormatRules(matchFormat)
  const scoreOptions = Array.from({ length: maxGamesPerSet + 1 }, (_, i) => i)

  // Get valid opponent scores based on one player's score
  const getValidOpponentScores = (playerScore) => {
    if (playerScore === null || playerScore === undefined) {
      return scoreOptions
    }

    // Check all possible opponent scores and return only valid ones
    const validScores = []
    for (
      let opponentScore = 0;
      opponentScore <= maxGamesPerSet;
      opponentScore++
    ) {
      if (isValidSetScore(playerScore, opponentScore, matchFormat)) {
        validScores.push(opponentScore)
      }
    }
    return validScores
  }

  // Helper function to render score options for picker
  const renderScoreOptions = (opponentScore) => {
    return scoreOptions.map((score) => {
      const validOptions = getValidOpponentScores(opponentScore)
      const isValid =
        opponentScore === null ||
        opponentScore === undefined ||
        validOptions.includes(score)
      return (
        <option key={score} value={score} disabled={!isValid}>
          {score}
        </option>
      )
    })
  }

  const handleLeftChange = (e) => {
    const leftVal = e.target.value === '' ? null : parseInt(e.target.value)
    onScoreChange(setIndex, leftVal, currentSet.right)
  }

  const handleRightChange = (e) => {
    const rightVal = e.target.value === '' ? null : parseInt(e.target.value)
    onScoreChange(setIndex, currentSet.left, rightVal)
  }

  return (
    <>
      <div
        className="picker-overlay"
        onClick={onClose}
        aria-hidden="true"
      ></div>
      <div
        className="picker-bottom-sheet"
        role="dialog"
        aria-labelledby="picker-title"
        aria-modal="true"
      >
        <div className="picker-header">
          <h5 id="picker-title">Set {setIndex + 1}</h5>
        </div>
        <div className="picker-body">
          <div className="picker-players">
            <div className="picker-player">
              <div className="player-label">{playerNames.left}</div>
              <select
                className="form-select picker-select"
                value={currentSet.left ?? ''}
                onChange={handleLeftChange}
                aria-label={`Select score for ${playerNames.left}`}
              >
                <option value="">—</option>
                {renderScoreOptions(currentSet.right)}
              </select>
            </div>
            <div className="picker-separator">–</div>
            <div className="picker-player">
              <div className="player-label">{playerNames.right}</div>
              <select
                className="form-select picker-select"
                value={currentSet.right ?? ''}
                onChange={handleRightChange}
                aria-label={`Select score for ${playerNames.right}`}
              >
                <option value="">—</option>
                {renderScoreOptions(currentSet.left)}
              </select>
            </div>
          </div>
        </div>
        <div className="picker-footer">
          <button className="btn btn-outline-secondary" onClick={onClear}>
            Clear Set
          </button>
          <button
            className="btn btn-primary"
            onClick={onClose}
            disabled={
              currentSet.left !== null &&
              currentSet.right !== null &&
              !isValidSetScore(currentSet.right, currentSet.left, matchFormat)
            }
          >
            Done
          </button>
        </div>
      </div>
    </>
  )
}

ScorePicker.propTypes = {
  visible: PropTypes.bool.isRequired,
  setIndex: PropTypes.number,
  sets: PropTypes.arrayOf(
    PropTypes.shape({
      left: PropTypes.number,
      right: PropTypes.number,
    }),
  ).isRequired,
  playerNames: PropTypes.shape({
    left: PropTypes.string.isRequired,
    right: PropTypes.string.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onScoreChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  matchFormat: PropTypes.string,
}

ScorePicker.defaultProps = {
  matchFormat: MATCH_FORMAT.TRADITIONAL,
}
